"use client";

import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { XIcon } from "@/app/docs/docs-icons";

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

type Props = {
  /** False triggers the slide-out; the parent unmounts after the transition. */
  present: boolean;
  onClose: () => void;
  labelledById: string;
  describedById?: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
};

/**
 * Reusable right-side dialog for the pricing worked cost comparison.
 *
 * Presentational only — the parent manager owns which drawer is mounted, the
 * exit timing and focus restoration. On mount this component locks background
 * scroll, moves focus in, wires Escape and traps Tab. `present` drives the
 * slide transition (enter runs on mount, exit when the parent flips it false).
 *
 * Respects prefers-reduced-motion (transitions removed via motion-reduce:).
 */
export function CostComparisonDrawer({
  present,
  onClose,
  labelledById,
  describedById,
  title,
  subtitle,
  children,
  footer,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [slidIn, setSlidIn] = useState(false);

  // Enter transition: mount off-screen, then slide in. setTimeout (not rAF) so
  // it still fires if the tab is backgrounded at mount time. `shown` also
  // depends on `present`, so the parent flipping `present` false slides it out.
  useEffect(() => {
    const id = window.setTimeout(() => setSlidIn(true), 20);
    return () => window.clearTimeout(id);
  }, []);

  // Lock background scroll for the drawer's lifetime (scroll position retained).
  useEffect(() => {
    const html = document.documentElement;
    const previous = html.style.overflow;
    html.style.overflow = "hidden";
    return () => {
      html.style.overflow = previous;
    };
  }, []);

  // Move focus to the close button once mounted.
  useEffect(() => {
    const id = window.setTimeout(() => closeRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);

  // Escape closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const shown = present && slidIn;

  function onPanelKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const items = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE),
    ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !panel.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      className={`fixed inset-0 z-[70] ${shown ? "" : "pointer-events-none"}`}
      aria-hidden={shown ? undefined : true}
    >
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-brand-navy/50 transition-opacity duration-200 motion-reduce:transition-none ${
          shown ? "opacity-100" : "opacity-0"
        }`}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        aria-describedby={describedById}
        onKeyDown={onPanelKeyDown}
        className={`absolute inset-y-0 right-0 flex w-full flex-col bg-surface-container-lowest shadow-2xl transition-transform duration-300 ease-out motion-reduce:transition-none sm:w-[88vw] lg:w-[62vw] lg:max-w-[1020px] lg:min-w-[760px] ${
          shown ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-outline-variant/60 px-5 py-4 sm:px-7">
          <div className="min-w-0">
            <h2
              id={labelledById}
              className="text-lg font-bold tracking-tight text-brand-navy"
            >
              {title}
            </h2>
            {subtitle && (
              <p
                id={describedById}
                className="mt-1.5 text-[13px] leading-6 text-on-surface-variant"
              >
                {subtitle}
              </p>
            )}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close comparison"
            className="-mr-1.5 shrink-0 rounded-md p-1.5 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-brand-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-electric"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-14 pt-6 sm:px-7">
          {children}
        </div>

        {footer && (
          <div className="border-t border-outline-variant/60 bg-surface-container-low/60 px-5 py-4 sm:px-7">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
